/// <amd-module name="Repos/Views/Settings/Repositories/Services/RepositoriesViewService" />
import { TaskCheckConstants } from "Checks/ChecksOverview/Generated/Constants";
import { CheckIssue, CheckIssueData } from "Checks/Client/Contracts/PipelinesChecks";
import { GitRepository } from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { IProjectRepositoryInformationService } from "Repos/Common/ProjectRepositoryInformationService";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef, tagNameToRef } from "Repos/Common/Util/Ref";
import { SimpleGitRef } from "Repos/Views/Settings/Branches/Data/SimpleGitRef";
import { ISettingsViewService, SettingsQueryStringParams, SettingsTabIds } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { DialogType, IPermissionsSet, IProject, IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import { FeatureFlagConstants, ResourceTypeNames } from "Repos/Views/Settings/Repositories/Generated/Constants";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { getGitClient } from "Repos/Views/Settings/Repositories/RestClient/Git";
import { ITfsPageService } from "Tfs/Platform/Page";
import {
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    IReadyableObservableArray,
    IReadyableReadonlyObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService, } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Filter, IFilter, IFilterState } from "VSSUI/Utilities/Filter";

const gitSecurableRoot = "repoV2/";
const gitSecurityNamespaceId = "2e9eb7ed-3c0a-47d4-87c1-0ffdd275fd87";
const tfvcSecurityNamespaceId = "a39371cf-0841-4c16-bbd3-276e341bc052";

export const permissionsAdditionalData: SimpleGitRef[] = [
    {
        ref: tagNameToRef(""),
        displayText: Resources.AllTags,
        iconProps: { iconName: "MultiSelect" }
    },
    {
        ref: branchNameToRef(""),
        displayText: Resources.AllBranches,
        iconProps: { iconName: "MultiSelect" }
    }
];

export interface IRepositoriesViewService extends IVssService {
    checkPermissionsAsync(permission: GitRepositoryPermissions, repositoryId?: string): Promise<boolean>;
    checkPermissions(permission: GitRepositoryPermissions): boolean;
    createRepository(createOperation: Promise<{ repository: GitRepository | { webUrl: string } }>, dismiss: () => void): void;
    deleteRepository(repository: IRepository): Promise<void>;
    getCheckScalabilityComplianceDeadline(): string | undefined;
    getDialogRepository(): IReadonlyObservableValue<IRepository | undefined>;
    getFilter(): IFilter;
    getFilteredRepositories(): IReadonlyObservableArray<IRepository | IReadonlyObservableValue<IRepository | undefined>>;
    getHideContentWhileLoad(): IReadonlyObservableValue<boolean>;
    getPermissionSet(ref?: string): IPermissionsSet;
    getPipelinesChecksData (repositoryId: string): Promise<void>;
    getProject(): IProject;
    getRepositories(): IReadyableReadonlyObservableArray<IRepository>;
    getSelected(): IReadonlyObservableValue<IRepository | undefined>;
    getSelectedRefs(): IReadonlyObservableValue<string | undefined>;
    getSelectedTab(): IReadonlyObservableValue<string | undefined>;
    getShownDialogType(): IReadonlyObservableValue<DialogType>;
    getStateKey(): string;
    renameRepository(repository: IRepository, newName: string): Promise<any>;

    /**
     * Using the individual set methods for selected repo, ref, and tab always causes a new page history entry for each change.
     * This method can be used to set all together with a single page history entry all changes.
     */
    setAllWithSinglePageHistoryEntry(repo: IRepository | string | undefined, branch: string | undefined, tab: string | undefined): void;
    setDialogRepository(value: IRepository | undefined): void;
    setSelected(value: IRepository | string | undefined, force?: boolean, skipReset?: boolean): void;
    setSelectedRefs(value: string | undefined): void;
    setSelectedTab(value?: string, skipNewHistoryEntry?: boolean): void;
    showDialog(type: DialogType, repository?: IRepository, onDismiss?: () => void): void;
}

export class RepositoriesViewService extends VssService implements IRepositoriesViewService {
    private readonly activeDialogType = new ObservableValue<DialogType>(DialogType.None);
    private dataCache: IReadyableObservableArray<IRepository>;
    private readonly dialogRepository = new ObservableValue<IRepository | undefined>(undefined);
    private readonly filter = new Filter();
    private readonly filteredData = new ObservableArray<IRepository | IReadonlyObservableValue<IRepository | undefined>>(
        new Array(5).fill(new ObservableValue<IRepository | undefined>(undefined))
    );
    private filterText = "";
    private readonly hideContentWhileLoad = new ObservableValue<boolean>(false);
    private onDialogDismiss: (() => void) | undefined;
    private pageHistoryService: IPageStateHistoryService;
    private readonly permissionsCache: { [key: string]: Promise<boolean> } = {};
    private preLoadSelectedId: string | undefined;
    private projectCache: IProject | undefined;
    private readonly selected = new ObservableValue<IRepository | undefined>(undefined);
    private readonly selectedRefs = new ObservableValue<string | undefined>(undefined);
    private readonly selectedTab = new ObservableValue<string | undefined>(undefined);
    private settingsSvc: ISettingsViewService;
    private tfvcEnabled: boolean = false;
    private checksScalabilityComplianceDeadline: string | undefined;

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
        this.filter.unsubscribe(this.onFilterChanged);

        this.pageHistoryService.unregisterObservable(SettingsQueryStringParams.repositoryId);
        this.pageHistoryService.unregisterObservable(SettingsQueryStringParams.tabId);
        this.pageHistoryService.unregisterObservable(SettingsQueryStringParams.refName);
    };

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.settingsSvc = this.pageContext.getService<ISettingsViewService>("SettingsViewService");
        this.filter.subscribe(this.onFilterChanged);

        this.pageHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this.pageHistoryService.registerObservable<IRepository | undefined>(
            this.selected,
            SettingsQueryStringParams.repositoryId,
            HistoryBehavior.newEntry,
            item => {
                return item ? item.id : "";
            },
            this.getRepositoryFromId
        );

        this.pageHistoryService.registerObservable<string | undefined>(
            this.selectedTab,
            SettingsQueryStringParams.tabId,
            HistoryBehavior.replace,
            item => {
                if (item === defaultTab || item === defaultTabMid) {
                    return "";
                }

                // prevent urls from changing even though we are changing the tab ids

                if (item === defaultTab || item === defaultTabMid || item === "repositories") {
                    return "";
                }

                if (item === SettingsTabIds.settings) {
                    return "settings";
                }

                return item || "";
            },
            selectedTab => {
                if (selectedTab === "") {
                    return undefined;
                }

                // prevent urls from changing even though we are changing the tab ids

                if (selectedTab === "repositories") {
                    return SettingsTabIds.repositories;
                }

                if (selectedTab === "settings") {
                    return SettingsTabIds.settings;
                }

                return selectedTab;
            }
        );

        this.pageHistoryService.registerObservable<string | undefined>(
            this.selectedRefs,
            SettingsQueryStringParams.refName,
            HistoryBehavior.replace,
            item => {
                return item || "";
            },
            selectedRefs => (selectedRefs === "" ? undefined : selectedRefs)
        );

        const projectRepoInfoSvc = pageContext.getService<IProjectRepositoryInformationService>("IProjectRepositoryInformationService");
        const vcData = projectRepoInfoSvc.getData();

        this.tfvcEnabled = !!vcData && vcData.supportsTfvc;
    };

    public checkPermissions = (permission: GitRepositoryPermissions): boolean => {
        const project = this.getProject();
        const permissionService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");

        return permissionService.hasPermission(permission, undefined, "", project.id);
    };

    public checkPermissionsAsync = (permission: GitRepositoryPermissions, repositoryId?: string): Promise<boolean> => {
        const cacheKey = permission + (repositoryId || "");
        let result = this.permissionsCache[cacheKey];

        if (result === undefined) {
            const project = this.getProject();
            const permissionService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");

            result = permissionService.hasPermissionAsync(permission, undefined, repositoryId || "", project.id);
            this.permissionsCache[cacheKey] = result;
        }

        return result;
    };

    public createRepository(createOperation: Promise<{ repository: GitRepository | { webUrl: string } }>, dismiss: () => void) {
        createOperation.then(result => {
            let itemToAdd: IRepository;
            if (isGitRepository(result.repository)) {
                const repository = result.repository;

                itemToAdd = convertGitRepoToIRepo(repository);
            } else {
                itemToAdd = this.getTfvcItem();
            }
            if (this.selected.value) {
                this.setSelected(itemToAdd);
            }

            this.dataCache.push(itemToAdd);
            this.dataCache.value = this.dataCache.value.sort((a: IRepository, b: IRepository) => compareRepositories(a, b));

            this.onFilterChanged(this.filter.getState()); // trigger fake filter change to update shown items so new item is added
            dismiss();
        });
    }

    public deleteRepository = (repository: IRepository): Promise<void> => {
        const gitClient = getGitClient(this.pageContext);

        return gitClient
            .deleteRepository(repository.id, this.getProject().id)
            .catch(reason => {
                this.settingsSvc.showErrorMessage(reason);
                return false;
            })
            .then(reponse => {
                if (reponse !== false) {
                    // remove current item from list
                    this.dataCache.value = this.dataCache.value.filter(item => item.id !== repository.id);
                    if (this.selected.value && this.selected.value.id === repository.id) {
                        // if deleting currently selected, need to update selected to first item
                        if (this.filteredData.length >= 1 && (this.filteredData.value[0] as IRepository).id !== repository.id) {
                            this.setSelected(this.filteredData.value[0] as IRepository);
                        } else if (this.filteredData.length >= 2) {
                            this.setSelected(this.filteredData.value[1] as IRepository);
                        } else {
                            // no items left, so clear selected
                            this.setSelected(undefined);
                        }
                    }

                    this.onFilterChanged(this.filter.getState()); // trigger fake filter change to update shown items so deleted item is removed
                }
            });
    };

    public getDialogRepository = (): IReadonlyObservableValue<IRepository | undefined> => {
        return this.dialogRepository;
    };

    public getFilter = (): IFilter => {
        return this.filter;
    };

    public getFilteredRepositories = (): IReadonlyObservableArray<IRepository | IReadonlyObservableValue<IRepository | undefined>> => {
        this.getRepositories();
        return this.filteredData;
    };

    public getHideContentWhileLoad(): IReadonlyObservableValue<boolean> {
        return this.hideContentWhileLoad;
    }

    public getPermissionSet(ref: string): IPermissionsSet {
        const project = this.getProject();
        const selected = this.getSelected().value;
        let refToken = "";

        if (selected && ref && ref.length > 0) {
            if (ref[ref.length - 1] === "/") {
                // if last char is a / trim it off
                ref = ref.substr(0, ref.length - 1);
            }
            refToken = `/${ref.split("/").join("^")}/`;
        }

        if (selected && selected.isTfvc) {
            return {
                id: tfvcSecurityNamespaceId,
                token: selected.name
            };
        }

        return {
            id: gitSecurityNamespaceId,
            token: `${gitSecurableRoot}${project.id}/${selected ? selected.id : ""}${refToken}`
        };
    }

    public getPipelinesChecksData = (repositoryId: string): Promise<void> => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync("ms.vss-pipelinechecks.checks-data-provider", { repositoryId }, true);
    }

    public getProject = (): IProject => {
        if (!this.projectCache) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageServiceData = tfsPageService.getData();
            const project = pageServiceData && pageServiceData.project;

            this.projectCache = {
                name: project && project.name,
                id: project && project.id
            };
        }
        return this.projectCache;
    };

    public getRepositories = (): IReadyableReadonlyObservableArray<IRepository> => {
        if (!this.dataCache) {
            this.dataCache = new ReadyableObservableArray<IRepository>([]);

            const gitClient = getGitClient(this.pageContext);
            const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            const scenario = performanceService.startScenario("repositories-settings-service-get-data", false);
            gitClient
                .getRepositories(this.getProject().id)
                .catch(this.settingsSvc.showErrorMessage)
                .then(restData => {
                    if (restData) {
                        const convertedData: IRepository[] = restData.map(gitRepo => convertGitRepoToIRepo(gitRepo));

                        if (this.tfvcEnabled) {
                            convertedData.push(this.getTfvcItem());
                        }

                        this.updateMissingRepoData(convertedData);
                    }
                    performanceService.endScenario("Repos", scenario.scenarioName, false);
                });
        }

        return this.dataCache;
    };

    public getSelected = (): IReadonlyObservableValue<IRepository | undefined> => {
        return this.selected;
    };

    public getSelectedRefs = (): IReadonlyObservableValue<string | undefined> => {
        return this.selectedRefs;
    };

    public getSelectedTab = (): IReadonlyObservableValue<string | undefined> => {
        return this.selectedTab;
    };

    public getShownDialogType = (): IReadonlyObservableValue<DialogType> => {
        return this.activeDialogType;
    };

    public getStateKey = (): string => {
        return `${this.getSelected().value ? this.getSelected().value!.id : "none"}/${
            this.getSelectedRefs().value ? this.getSelectedRefs().value + "/" : ""
        }${this.getSelectedTab().value ? this.getSelectedTab().value : ""}`;
    };

    public renameRepository = (repository: IRepository, newName: string): Promise<IRepository | undefined> => {
        const gitClient = getGitClient(this.pageContext);
        const renameRepo = getRenameGitRepository(repository);
        renameRepo.name = newName;
        return gitClient
            .updateRepository(renameRepo, renameRepo.id, this.getProject().id)
            .catch(this.settingsSvc.showErrorMessage)
            .then((freshRepo: GitRepository) => {
                if (freshRepo) {
                    // update the current item
                    const item = this.dataCache.value.find((item: IRepository) => item.id === freshRepo.id);
                    if (item) {
                        item.name = freshRepo.name;
                        item.webUrl = freshRepo.webUrl;
                        this.onFilterChanged(this.filter.getState()); // trigger fake filter change to update shown items so new name is shown
                        if (this.getSelected().value == item) {
                            // force refresh on rename
                            this.setSelected(item, true);
                        }
                    }

                    return item;
                }
            });
    };

    public setAllWithSinglePageHistoryEntry = (repo: IRepository | string | undefined, branch: string | undefined, tab: string | undefined) => {
        const skipForRef = this.selected.value != repo;
        const skipForTab = skipForRef || this.selectedRefs.value != branch;
        this.setSelected(repo, false, true);
        this.setSelectedRefs(branch, skipForRef);
        this.setSelectedTab(tab, skipForTab);
    };

    public setDialogRepository = (value: IRepository | undefined) => {
        this.dialogRepository.value = value;
    };

    public setSelected = (value: IRepository | string | undefined, force?: boolean, skipReset?: boolean) => {
        if (this.isReady()) {
            if (isString(value)) {
                value = this.getRepositoryFromId(value);
            }

            if (this.selected.value !== value || force) {
                this.selected.value = value as IRepository;

                if (!skipReset) {
                    this.setSelectedRefs(undefined, true);

                    if (this.selected.value && this.selected.value.isTfvc) {
                        // tfvc page doesn't have all the same tabs, so lets clear it
                        this.setSelectedTab("");
                    }
                }
            }
        } else if (value) {
            if (isString(value)) {
                this.preLoadSelectedId = value;
            } else {
                this.preLoadSelectedId = value.id;
            }
        }
    };

    public setSelectedRefs = (value: string | undefined, skipNewHistoryEntry?: boolean) => {
        if (this.selectedRefs.value !== value) {
            !skipNewHistoryEntry &&
                this.pageHistoryService.queueValueChange(SettingsQueryStringParams.refName, value || "", HistoryBehavior.newEntry);
            this.selectedRefs.value = value;
        }
    };

    public setSelectedTab = (value?: string, skipNewHistoryEntry?: boolean) => {
        if (this.selectedTab.value !== value) {
            !skipNewHistoryEntry && this.pageHistoryService.queueValueChange(SettingsQueryStringParams.tabId, value || "", HistoryBehavior.newEntry);
            this.selectedTab.value = value;
        }
    };

    public showDialog = (dialogType: DialogType, repository?: IRepository, onDismiss?: () => void) => {
        if (dialogType === DialogType.None || !repository) {
            // if repository is not provided, then can not set show dialog type
            this.activeDialogType.value = DialogType.None;
            this.setDialogRepository(undefined);

            if (this.onDialogDismiss) {
                this.onDialogDismiss();
                this.onDialogDismiss = undefined;
            }
        } else {
            this.onDialogDismiss = onDismiss;
            this.activeDialogType.value = dialogType;
            this.setDialogRepository(repository);
        }
    };

    private getRepositoryFromId = (id: string): IRepository | undefined => {
        if (this.isReady()) {
            return this.dataCache.value.find(item => item.id === id);
        }

        this.getRepositories();
        this.preLoadSelectedId = id;
        this.hideContentWhileLoad.value = true;

        return undefined;
    };

    private updateMissingRepoData(convertedData: IRepository[]) {
        const _showCheckIssues = isFeatureFlagEnabled(
            this.pageContext,
            FeatureFlagConstants.EnableChecksScalabilityResourceIssues,
            false
        );

        this.updateCachedRepoData(convertedData);

        if (_showCheckIssues && convertedData?.length > 0){
            this.updateCachedRepoDataWithCheckIssues(convertedData);
        }
    }

    private updateCachedRepoData(convertedData: IRepository[]) {
        this.dataCache.value = convertedData.sort((a: IRepository, b: IRepository) => compareRepositories(a, b));

        this.filteredData.value = filterRepoArr(this.dataCache.value, this.filterText);

        this.dataCache.ready.value = true;

        if (this.preLoadSelectedId) {
            const foundItem = this.dataCache.value.find((value: IRepository) => value.id === this.preLoadSelectedId);
            if (foundItem) {
                this.selected.value = foundItem;
            } else {
                // clear child in case this link is stale
                this.setSelectedRefs(undefined);
            }
        }
        this.hideContentWhileLoad.value = false;
    }

    private updateCachedRepoDataWithCheckIssues(repositories: IRepository[]): void {
        let resourceParams: { [key: string]: string; } = {
            "resourceTypeName": ResourceTypeNames.Repository,
            "resourceIds": repositories
                .filter(r => r.gitRepository !== undefined)
                .map(r => this.getRepoResourceId(r)).join(",")
        };
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService
            .getDataAsync<CheckIssueData>("ms.vss-pipelinechecks.checks-issues-provider", resourceParams)
            .then((checkIssueDataResult: CheckIssueData) => {
                const checkIssuesHashMap = checkIssueDataResult?.resourcesWithCheckIssuesList
                    .reduce((hashMap : {[key: string]: CheckIssue}, resourceWithCheckIssue) => {
                        const {id} = resourceWithCheckIssue.resource;
                        if (!hashMap[id]){
                            hashMap[id] = resourceWithCheckIssue.checkIssue;
                        }
                        return hashMap;
                    }, {});

                if (checkIssuesHashMap && Object.keys(checkIssuesHashMap).length > 0){
                    this.dataCache.value.forEach(repository => {
                        const repositoryCheckIssue = checkIssuesHashMap[this.getRepoResourceId(repository)];
                        repository.issue = repositoryCheckIssue;
                    });
                }
                this.checksScalabilityComplianceDeadline = checkIssueDataResult.defaultCheckIssueSettings[TaskCheckConstants.TaskCheckScalabilityComplianceDeadline];
            });
    }

    public getCheckScalabilityComplianceDeadline(): string | undefined {
        return this.checksScalabilityComplianceDeadline;
    }

    private getRepoResourceId(repository: IRepository): string {
        return repository.gitRepository?.project.id + "." + repository.id;
    }

    private getTfvcItem() {
        const routingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const project = this.getProject();
        const url = routingService.getSwitchRepositoryURL(null, project.name);

        const tfvcItem: IRepository = {
            name: "$/" + project.name,
            id: "tfvc",
            iconProps: { iconName: "TFVCLogo", ariaLabel: Resources.Tfvc, className: "margin-right-8" },
            isTfvc: true,
            webUrl: url
        };

        return tfvcItem;
    }

    private isReady = () => {
        return this.dataCache && this.dataCache.ready && this.dataCache.ready.value;
    };

    private onFilterChanged = (state: IFilterState) => {
        if (this.isReady()) {
            const filterItem = state["name"];

            if (filterItem) {
                this.filterText = filterItem.value;
                this.filteredData.value = filterRepoArr(this.dataCache.value, this.filterText);
            } else {
                this.filterText = "";
                this.filteredData.value = this.dataCache.value;
            }
        }
    };
}

const defaultTab = SettingsTabIds.repositories;
const defaultTabMid = SettingsTabIds.settingsMid;

function compareRepositories(a: IRepository, b: IRepository) {
    // ensure TFVC always top
    if (a.name.startsWith("$")) {
        return -1;
    }

    if (b.name.startsWith("$")) {
        return 1;
    }

    return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase(), navigator.language);
}

function convertGitRepoToIRepo(gitRepo: GitRepository): IRepository {
    return {
        id: gitRepo.id,
        name: gitRepo.name,
        iconProps: {
            iconName: gitRepo.isFork ? "GitFork" : "GitLogo",
            ariaLabel: Resources.Git,
            className: gitRepo.isFork ? "margin-right-4" : "margin-right-8"
        },
        isFork: gitRepo.isFork,
        webUrl: gitRepo.webUrl,
        defaultBranch: gitRepo.defaultBranch,
        gitRepository: gitRepo
    };
}

function filterRepoArr(arr: IRepository[], filter: string): IRepository[] {
    return arr.filter((item: IRepository) => item.name.toLocaleLowerCase().indexOf(filter.toLocaleLowerCase()) >= 0);
}

function getRenameGitRepository(repository: IRepository): GitRepository {
    return { id: repository.id } as GitRepository;
}

function isGitRepository(x: GitRepository | { webUrl: string }): x is GitRepository {
    return (x as GitRepository).id ? true : false;
}

function isString(s: any): s is string {
    return typeof s === "string";
}

Services.add("RepositoriesViewService", { serviceFactory: RepositoriesViewService });
